import { PhaseColor } from './Timeline.types';

export function getBaseColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-300';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
            return '--color-orange-200';

        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-300';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
            return '--color-sky-300';

        case 'Event':
            return '--color-sky-800';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-300';

        case 'Project':
            return '--color-rose-500';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
            return '--color-rose-300';

        case 'External-Event-Category-Project':
            return '--color-rose-500';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
            return '--color-sky-300';

        case 'External-Event-Category-Event':
            return '--color-sky-800';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
            return '--color-green-300';

        case 'External-Event-Category-External-Event':
            return '--color-green-700';

        case 'Aggregation':
            return '--color-gray-600';

        default:
            throw new Error(`Unknown phase color: ${color} (getBaseColorForPhaseType())`);
    }
}

export function getPlus100ColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-400';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
            return '--color-orange-300';

        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-400';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
            return '--color-sky-400';

        case 'Event':
            return '--color-sky-900';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-400';

        case 'Project':
            return '--color-rose-600';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
            return '--color-rose-400';

        case 'External-Event-Category-Project':
            return '--color-rose-600';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
            return '--color-sky-400';

        case 'External-Event-Category-Event':
            return '--color-sky-900';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
            return '--color-green-400';

        case 'External-Event-Category-External-Event':
            return '--color-green-800';

        case 'Aggregation':
            return '--color-gray-700';

        default:
            throw new Error(`Unknown phase color: ${color} (getPlus100ColorForPhaseType())`);
    }
}

export function getPlus200ColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-500';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
            return '--color-orange-400';

        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-500';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
            return '--color-sky-500';

        case 'Event':
            return '--color-sky-950';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-500';

        case 'Project':
            return '--color-rose-700';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
            return '--color-rose-500';

        case 'External-Event-Category-Project':
            return '--color-rose-700';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
            return '--color-sky-500';

        case 'External-Event-Category-Event':
            return '--color-sky-950';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
            return '--color-green-500';

        case 'External-Event-Category-External-Event':
            return '--color-green-900';

        case 'Aggregation':
            return '--color-gray-800';

        default:
            throw new Error(`Unknown phase color: ${color} (getPlus200ColorForPhaseType())`);
    }
}

export function getTextColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Project-Set-Up':
        case 'Project-Breakdown':
        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
            return '--color-gray-900';

        case 'External-Event-Category-Project':
        case 'External-Event-Category-Event':
        case 'External-Event-Category-External-Event':
        case 'Event':
        case 'Project':
        case 'Aggregation':
            return '--color-gray-50';

        default:
            throw new Error(`Unknown phase color: ${color} (getTextColorForPhaseType())`);
    }
}

/**
 * Switches between allocation types (identified via the corresponding phase type): Event | Project | External Event | Aggregation
 */
export function getAllocationColor100ForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-100';

        case 'Project':
        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-100';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-100';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-100';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-100';

        case 'Aggregation':
            return '--color-gray-100';

        default:
            throw new Error(`Unknown phase color: ${color} (getAllocationColor100ForPhaseType())`);
    }
}

/**
 * Switches between allocation types (identified via the corresponding phase type): Event | Project | External Event | Aggregation
 */
export function getAllocationColor200ForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-200';

        case 'Project':
        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-200';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-200';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-200';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-200';

        case 'Aggregation':
            return '--color-gray-200';

        default:
            throw new Error(`Unknown phase color: ${color} (getAllocationColor200ForPhaseType())`);
    }
}

/**
 * Switches between allocation types (identified via the corresponding phase type): Event | Project | External Event | Aggregation
 */
export function getAllocationColor300ForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-300';

        case 'Project':
        case 'Project-Set-Up':
        case 'Project-Breakdown':
            return '--color-rose-300';

        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-300';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-300';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-300';

        case 'Aggregation':
            return '--color-gray-300';

        default:
            throw new Error(`Unknown phase color: ${color} (getAllocationColor300ForPhaseType())`);
    }
}

export function get50ColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-50';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-50';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-50';

        case 'Aggregation':
            return '--color-gray-50';

        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-50';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-50';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-50';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
        case 'Project':
            return '--color-rose-50';

        default:
            throw new Error(`Unknown phase color: ${color} (get50ColorForPhaseType())`);
    }
}

export function get700ColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-700';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-700';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-700';

        case 'Aggregation':
            return '--color-gray-700';

        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-700';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-700';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-700';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
        case 'Project':
            return '--color-rose-700';

        default:
            throw new Error(`Unknown phase color: ${color} (get700ColorForPhaseType())`);
    }
}

export function get900ColorForPhaseType(color: PhaseColor): string {
    switch (color) {
        case 'Pre-External-Event-Category-Project':
        case 'Post-External-Event-Category-Project':
        case 'External-Event-Category-Project':
            return '--color-rose-900';

        case 'Pre-External-Event-Category-Event':
        case 'Post-External-Event-Category-Event':
        case 'External-Event-Category-Event':
            return '--color-sky-900';

        case 'Pre-External-Event-Category-External-Event':
        case 'Post-External-Event-Category-External-Event':
        case 'External-Event-Category-External-Event':
            return '--color-green-900';

        case 'Aggregation':
            return '--color-gray-900';

        case 'Technical-Set-Up':
        case 'Technical-Breakdown':
            return '--color-amber-900';

        case 'Extended-Technical-Set-Up':
        case 'Extended-Technical-Breakdown':
        case 'Early-Set-Up':
        case 'Extended-Breakdown':
            return '--color-orange-900';

        case 'Regular-Set-Up':
        case 'Regular-Breakdown':
        case 'Event':
            return '--color-sky-900';

        case 'Project-Set-Up':
        case 'Project-Breakdown':
        case 'Project':
            return '--color-rose-900';

        default:
            throw new Error(`Unknown phase color: ${color} (get900ColorForPhaseType())`);
    }
}
